import React, {useEffect, useState} from 'react';
import {Box, Grid, styled, Typography} from '@mui/material';
import {ImageValue} from "../../../models/Template";
import Button from "@mui/material/Button";
import {SelectedImageData, useTemplate} from "../../../context/TemplateContext";
import {useTranslation} from "react-i18next";
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CropIcon from '@mui/icons-material/Crop';

interface ImageSelectorProps {
    elementKey: string;
    label: string;
    value: ImageValue
    folderId: string;
    onClick: (imageData: SelectedImageData) => void;
    onImageTransformClick: (key: string | undefined) => void;
    onImageTransformResetClick: (key: string, value: ImageValue) => void;
    allowImageTransform: boolean;
    selectedDesignKey: string;
}

// Styled components
const ImageContainer = styled(Grid)(({theme}) => ({
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
}));

const ImagePreview = styled(Box)(({theme}) => ({
    width: '100%',
    aspectRatio: '1',
    borderRadius: '8px',
    overflow: 'hidden',
    position: 'relative',
    backgroundColor: '#141b33',
    border: '1px solid #ffffffA8',
    marginBottom: theme.spacing(1),
}));

const SelectButton = styled(Button)(({theme}) => ({
    height: '32px',
    backgroundColor: 'white',
    color: '#141b33',
    borderRadius: '100px',
    padding: '8px 16px',
    textTransform: 'none',
    fontWeight: 500,
    margin: theme.spacing(1, 0),
    '&:hover': {
        backgroundColor: '#f0f0f0',
    },
}));

const SimpleButton = styled(Button)(({theme}) => ({
    height: '32px',
    color: 'white',
    borderRadius: '100px',
    padding: '8px 16px',
    textTransform: 'none',
    fontWeight: 500,
    '&:hover': {
        backgroundColor: '#1f2a4a',
    },
}));

const PreviewImage = styled('img')({
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: 'block',
});

const ImageSelector: React.FC<ImageSelectorProps> = ({
                                                         elementKey,
                                                         label,
                                                         value,
                                                         folderId,
                                                         onClick,
                                                         onImageTransformClick,
                                                         onImageTransformResetClick,
                                                         allowImageTransform,
                                                         selectedDesignKey
                                                     }) => {
    const [dimensions, setDimensions] = useState({width: 0, height: 0});
    const [hover, setHover] = useState(false);
    const {selectedTransformedImage, setImageTransforms, template} = useTemplate();
    const {t} = useTranslation();

    useEffect(() => {
        const element = document.querySelector(`[data-name="${elementKey}"]`) as HTMLElement;
        if (element && element.style.width && element.style.height) {
            setDimensions({
                width: Number(element.style.width.replace('px', '')),
                height: Number(element.style.height.replace('px', ''))
            });
        }
    }, [elementKey]);

    const handleMouseEnter = () => setHover(true);
    const handleMouseLeave = () => setHover(false);

    const handleClick = () => {
        onClick({
            elementKey,
            label,
            value,
            folderId
        });
    };


    return (
        value && (
            <ImageContainer item sx={2} xs={6} md={4} data-testid={`image-input-${elementKey}`}>
                <ImagePreview
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                    onClick={handleClick}
                >
                    <PreviewImage src={value.thumbnail} alt={value.name}/>
                    <Box
                        sx={{
                            position: 'absolute',
                            bottom: 0,
                            left: 0,
                            right: 0,
                            bgcolor: 'rgba(20, 27, 51, 0.7)',
                            py: 0.5,
                            px: 2
                        }}
                    >
                        <Typography
                            variant="body2"
                            sx={{
                                color: 'white',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            {value.name}
                        </Typography>
                    </Box>
                </ImagePreview>

                <SelectButton
                    variant="contained"
                    data-testid={`image-gallery-${elementKey}`}
                    startIcon={<AttachFileIcon/>}
                    onClick={handleClick}
                    fullWidth
                >
                    {t('editor.imageSelector.gallery')}
                </SelectButton>

                {allowImageTransform && (
                    selectedTransformedImage !== elementKey ? (
                        <SimpleButton
                            sx={{
                                border: '1px solid #ffffffA8',
                            }}
                            variant="outlined"
                            data-testid={`image-reposition-${elementKey}`}
                            startIcon={<CropIcon/>}
                            onClick={() => onImageTransformClick(elementKey)}
                            fullWidth
                        >
                            {t('editor.imageSelector.reposition')}
                        </SimpleButton>
                    ) : (
                        <Box sx={{display: 'flex'}}>
                            <SimpleButton
                                sx={{flex: 1, marginRight: '3px'}}
                                variant="contained"
                                color="primary"
                                onClick={() => onImageTransformClick(undefined)}
                            >
                                {t("editor.imageSelector.confirm")}
                            </SimpleButton>
                            <SimpleButton
                                sx={{flex: 1, marginLeft: '3px'}}
                                variant="outlined"
                                color="error"
                                onClick={() => onImageTransformResetClick(elementKey, value)}
                            >
                                {t("editor.imageSelector.reset")}
                            </SimpleButton>
                        </Box>
                    )
                )}
            </ImageContainer>
        )
    );
};

export default ImageSelector;